import { FC, ReactNode, useCallback } from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { FloorMap } from 'src/components/FloorMap/FloorMap';
import { RenderBookingObjectParams } from 'src/components/FloorMap/types';
import { getAgentState } from 'src/store/agent/selectors';
import { getSpacesState } from 'src/store/spaces/selectors';
import { MapInfo } from 'src/store/spaces/types';
import { BookingObject, PlaceType } from 'src/types';

import { supportedPlaceTypes } from '../../consts';
import { SpaceDeskPlace } from '../SpaceDeskPlace/SpaceDeskPlace';
import { SpaceParkingPlace } from '../SpaceParkingPlace/SpaceParkingPlace';
import { SpaceBookingObjectOption } from '../types';

export const renderBookingObject = ({
  bookingObject,
}: RenderBookingObjectParams<BookingObject>): ReactNode => {
  switch (bookingObject.type) {
    case PlaceType.Desk:
      return (
        <SpaceDeskPlace key={bookingObject.id ?? 'new'} item={bookingObject} />
      );
    case PlaceType.Parking:
      return (
        <SpaceParkingPlace
          key={bookingObject.id ?? 'new'}
          item={bookingObject}
        />
      );
  }
};

export const SpaceFloorMap: FC = () => {
  const { touch } = useSelector(getAgentState);
  const { floor, town, building, mapLoading, bookingObjects, relatedSpaces } =
    useSelector(getSpacesState);

  const groups =
    useField<Map<number, SpaceBookingObjectOption[]>>('groups').input.value;
  const groupInfos = useField<Map<number, MapInfo>>('groupInfos').input.value;
  const { change } = useForm();

  const toggle = useCallback(
    async (id: string) => {
      if (!floor || !building || !town) return;

      const geoObject = bookingObjects.get(id);

      if (
        !geoObject?.type ||
        !supportedPlaceTypes.has(geoObject.type) ||
        !geoObject.title
      )
        return;

      const nextGroups = new Map(groups);
      const nextGroupInfos = new Map(groupInfos);

      const group = groups.get(floor.value.floorId) ?? [];

      const desks = new Map(group.map(d => [d.id, d]));
      if (desks.has(geoObject.id!)) {
        desks.delete(geoObject.id!);
      } else {
        desks.set(geoObject.id!, {
          id: geoObject.id!,
          value: {
            title: geoObject.title,
            space: geoObject.spaceId
              ? (relatedSpaces.get(geoObject.spaceId) ?? null)
              : null,
          },
        });
      }
      const options = [...desks.values()];

      if (options.length) {
        nextGroups.set(floor.value.floorId, options);
        nextGroupInfos.set(floor.value.floorId, {
          floorId: floor.value.floorId,
          title: floor.value.title,
          buildingId: building.value.buildingId,
          buildingAddress: building.value.address,
          townId: town.value.townId,
          townName: town.value.townName,
        });
      } else {
        nextGroups.delete(floor.value.floorId);
        nextGroupInfos.delete(floor.value.floorId);
      }
      change('groups', nextGroups);
      change('groupInfos', nextGroupInfos);
    },
    [
      bookingObjects,
      building,
      change,
      floor,
      groupInfos,
      groups,
      relatedSpaces,
      town,
    ],
  );

  return (
    <FloorMap
      loading={mapLoading}
      bookingObjects={bookingObjects}
      onClickOnBookingObject={toggle}
      renderBookingObject={renderBookingObject}
      touchDevice={touch}
    />
  );
};
